<template>
    <div class="flex mt-[42px] mb-[27px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.my_profile") }}
        </h1>
    </div>

    <SettingsTabs class="mt-[24px]" tab-key="mailing-preferences">
        <div class="mb-[98px] mt-[25px]">
            <div class="mt-[42px] grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.mailing_preferences_title") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.mailing_preferences_description") }}
                    </p>
                </div>

                <div class="mx-auto w-full rounded-md bg-white shadow-card">
                    <div class="px-[21px] py-[21px]">
                        <div class="space-y-[24px]">
                            <div
                                v-for="item in preferenceItems"
                                :key="item.key"
                                class="flex items-start justify-between gap-6"
                            >
                                <div class="min-w-0">
                                    <p
                                        class="text-[14px] font-medium leading-[140%] text-[#161E2E]"
                                    >
                                        {{ $t(item.titleKey) }}
                                    </p>
                                    <p
                                        class="mt-[4px] text-[10.5px] font-normal leading-[142%] text-[#4B5563]"
                                    >
                                        {{ $t(item.descKey) }}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    class="flex h-[17px] w-[32px] items-center rounded-[999px] p-[2px] transition-colors"
                                    :class="
                                        preferences[item.key]
                                            ? 'bg-[#FF421E]'
                                            : 'bg-[#E5E7EB]'
                                    "
                                    :disabled="loading || saving"
                                    :aria-pressed="
                                        preferences[item.key] ? 'true' : 'false'
                                    "
                                    :aria-label="$t(item.titleKey)"
                                    @click="togglePreference(item.key)"
                                >
                                    <span
                                        class="h-[13px] w-[13px] rounded-full bg-white transition-transform"
                                        :class="
                                            preferences[item.key]
                                                ? 'translate-x-[15px]'
                                                : 'translate-x-0'
                                        "
                                    ></span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        class="flex justify-end border-t border-[#E5E7EB] px-[21px] py-[21px]"
                    >
                        <button
                            type="button"
                            class="h-[32px] rounded-[6px] px-[14px] text-[10.5px] font-medium leading-[142%] text-white transition"
                            :class="
                                hasChanges && !saving
                                    ? 'bg-[#088BEA] hover:bg-[#0679CC]'
                                    : 'cursor-not-allowed bg-[#E5E7EB] opacity-70'
                            "
                            :disabled="!hasChanges || saving"
                            @click="saveChanges"
                        >
                            {{
                                saving
                                    ? $t("vue.saving_in_process")
                                    : $t("vue.save_changes")
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </SettingsTabs>
</template>

<script lang="ts" setup>
import SettingsTabs from "@/Pages/Settings/components/SettingsTabs.vue";
import { computed, onMounted, ref } from "vue";
import axios from "axios";
import route from "ziggy-js";

type PreferenceKey = "marketing" | "insights" | "customer" | "product";

const preferenceItems: Array<{
    key: PreferenceKey;
    titleKey: string;
    descKey: string;
}> = [
    {
        key: "marketing",
        titleKey: "vue.mailing_pref_marketing_title",
        descKey: "vue.mailing_pref_marketing_desc",
    },
    {
        key: "insights",
        titleKey: "vue.mailing_pref_insights_title",
        descKey: "vue.mailing_pref_insights_desc",
    },
    {
        key: "customer",
        titleKey: "vue.mailing_pref_customer_title",
        descKey: "vue.mailing_pref_customer_desc",
    },
    {
        key: "product",
        titleKey: "vue.mailing_pref_product_title",
        descKey: "vue.mailing_pref_product_desc",
    },
];

const initialPreferences: Record<PreferenceKey, boolean> = {
    marketing: true,
    insights: true,
    customer: true,
    product: true,
};

const preferences = ref<Record<PreferenceKey, boolean>>({
    ...initialPreferences,
});
const originalPreferences = ref<Record<PreferenceKey, boolean>>({
    ...initialPreferences,
});
const loading = ref(true);
const saving = ref(false);

const hasChanges = computed(() =>
    preferenceItems.some(
        (item) =>
            preferences.value[item.key] !== originalPreferences.value[item.key],
    ),
);

const togglePreference = (key: PreferenceKey) => {
    if (loading.value || saving.value) return;
    preferences.value[key] = !preferences.value[key];
};

const loadPreferences = async () => {
    loading.value = true;

    try {
        const response = await axios.get(
            route("settings.mailing-preferences.preferences"),
            { headers: { Accept: "application/json" } },
        );

        const incoming = response?.data?.preferences;
        if (incoming && typeof incoming === "object") {
            preferences.value = {
                marketing: Boolean(incoming.marketing),
                insights: Boolean(incoming.insights),
                customer: Boolean(incoming.customer),
                product: Boolean(incoming.product),
            };
            originalPreferences.value = { ...preferences.value };
        }
    } catch {
        originalPreferences.value = { ...preferences.value };
    } finally {
        loading.value = false;
    }
};

const saveChanges = async () => {
    if (saving.value || !hasChanges.value) return;
    saving.value = true;

    try {
        await axios.put(
            route("settings.mailing-preferences.update"),
            { preferences: preferences.value },
            { headers: { Accept: "application/json" } },
        );

        originalPreferences.value = { ...preferences.value };
    } finally {
        saving.value = false;
    }
};

onMounted(loadPreferences);
</script>
